import {
  PieChartPropsType,
  pieColors,
  usePieChart,
} from '@euler/lib/charts-core';
import { useState } from 'react';
import { Text, TextStyle, View } from 'react-native';
import { RotatablePie } from '../Components/Rotatable';
import { isWebApp } from '../utils';
import { PieChartMain } from './main';

export const PieChart = (props: PieChartPropsType) => {
  if (!props.data) return null;
  /* eslint-disable react-hooks/rules-of-hooks */
  const {
    radius,
    extraRadius,
    selectedIndex,
    setSelectedIndex,
    startAngle,
    total,
    donut,
    isThreeD,
    semiCircle,
    inwardExtraLengthForFocused,
    canvasWidth,
    canvasHeight,
    innerRadius,
    innerCircleColor,
    innerCircleBorderWidth,
    innerCircleBorderColor,
    shiftInnerCenterX,
    shiftInnerCenterY,
    tiltAngle,
    isDataShifted,
    paddingHorizontal,
    paddingVertical,
    data,
    showTooltip,
    tooltipHorizontalShift,
    tooltipVerticalShift,
    tooltipComponent,
    getTooltipText,
    tooltipBackgroundColor,
    tooltipBorderRadius,
    tooltipWidth,
    tooltipTextNoOfLines,
    textColor,
    textSize,
    font,
    fontWeight,
    fontStyle,
    tooltipSelectedIndex,
    setTooltipSelectedIndex,
    rotatable,
  } = usePieChart(props);

  const [touchX, setTouchX] = useState(0);
  const [touchY, setTouchY] = useState(0);
  /* eslint-enable react-hooks/rules-of-hooks */

  const renderInnerCircle = (
    innerRadiusValue: number,
    innerCircleBorderWidthValue: number,
  ) => {
    if (props.centerLabelComponent || (donut && !isDataShifted)) {
      return (
        <View
          style={[
            {
              height: innerRadiusValue * 2,
              width: innerRadiusValue * 2,
              borderRadius: innerRadiusValue,
              position: 'absolute',
              // zIndex: 100,
              alignSelf: 'center',
              backgroundColor: innerCircleColor,
              left:
                canvasWidth / 2 -
                innerRadiusValue +
                shiftInnerCenterX +
                extraRadius +
                paddingHorizontal / 2,
              top:
                canvasHeight / 2 -
                innerRadiusValue +
                shiftInnerCenterY +
                extraRadius +
                paddingVertical / 2,
              borderWidth: innerCircleBorderWidthValue,
              borderColor: innerCircleBorderColor,
              justifyContent: 'center',
              alignItems: 'center',
            },
            isThreeD && {
              borderTopWidth: innerCircleBorderWidthValue * 5,
              borderLeftWidth: shiftInnerCenterX
                ? innerCircleBorderWidthValue * 2
                : innerCircleBorderWidthValue,
              transform: [{ rotateX: tiltAngle }],
            },
            semiCircle &&
              isThreeD && {
                borderTopWidth: isThreeD
                  ? innerCircleBorderWidthValue * 5
                  : innerCircleBorderWidthValue,
                borderLeftWidth: 0.5,
                borderLeftColor: innerCircleColor,
                borderBottomWidth: 0,
                borderRightWidth: 0.5,
                borderRightColor: innerCircleColor,
              },
          ]}
          pointerEvents="box-none"
        >
          <View style={{ marginTop: semiCircle ? -0.5 * innerRadiusValue : 0 }}>
            {props.centerLabelComponent?.(selectedIndex) ?? null}
          </View>
        </View>
      );
    }
    return null;
  };

  const renderTooltip = () => {
    return (
      <View
        style={{
          position: 'absolute',
          left:
            touchX > (radius + extraRadius) * 1.5
              ? props.tooltipHorizontalShift
                ? touchX - tooltipHorizontalShift
                : touchX -
                  (tooltipWidth ??
                    getTooltipText(tooltipSelectedIndex).length * 10)
              : touchX - tooltipHorizontalShift,
          top:
            touchY < 30
              ? props.tooltipVerticalShift
                ? touchY - tooltipVerticalShift
                : touchY
              : touchY - tooltipVerticalShift,
        }}
      >
        {data[tooltipSelectedIndex].tooltipComponent ? (
          data[tooltipSelectedIndex].tooltipComponent?.()
        ) : tooltipComponent ? (
          tooltipComponent(tooltipSelectedIndex)
        ) : (
          <View
            style={{
              backgroundColor: tooltipBackgroundColor,
              borderRadius: tooltipBorderRadius,
              paddingHorizontal: 8,
              paddingBottom: 8,
              paddingTop: 4,
              width: tooltipWidth,
            }}
          >
            <Text
              numberOfLines={tooltipTextNoOfLines}
              style={
                {
                  color:
                    data[tooltipSelectedIndex].textColor ||
                    textColor ||
                    'white',
                  textAlign: 'center',
                  fontSize: textSize,
                  fontFamily: font,
                  fontWeight,
                  fontStyle,
                } as TextStyle
              }
            >
              {getTooltipText(tooltipSelectedIndex)}
            </Text>
          </View>
        )}
      </View>
    );
  };

  const PieChartBody = () => {
    return (
      <View
        style={{
          height:
            (radius + extraRadius + paddingVertical / 2) *
            (props.semiCircle ? 1 : 2),
          width: (radius + extraRadius + paddingHorizontal / 2) * 2,
          overflow: 'hidden',
        }}
      >
        <View style={{ position: 'absolute' }}>
          <PieChartMain
            {...props}
            setTouchX={setTouchX}
            setTouchY={setTouchY}
            tooltipSelectedIndex={tooltipSelectedIndex}
            setTooltipSelectedIndex={setTooltipSelectedIndex}
            selectedIndex={selectedIndex}
            setSelectedIndex={setSelectedIndex}
            paddingHorizontal={paddingHorizontal}
            paddingVertical={paddingVertical}
            extraRadius={extraRadius}
          />
        </View>
        {renderInnerCircle(innerRadius, innerCircleBorderWidth)}
        {props.data.length > 1 &&
          props.data[selectedIndex] && // don't forget to add this one so there are no errors when the data is empty / updating
          (props.focusOnPress || props.sectionAutoFocus) &&
          selectedIndex !== -1 && (
            <View
              pointerEvents="box-none"
              style={{
                position: 'absolute',
                top: -extraRadius,
                left: -extraRadius,
                zIndex: isWebApp ? -1 : 0, // was not getting displayed in web (using Expo)
              }}
            >
              <PieChartMain
                {...props}
                setTouchX={setTouchX}
                setTouchY={setTouchY}
                tooltipSelectedIndex={tooltipSelectedIndex}
                setTooltipSelectedIndex={setTooltipSelectedIndex}
                data={[
                  {
                    ...props.data[selectedIndex],
                    color: props.showGradient
                      ? `url(#grad${selectedIndex})`
                      : props.data[selectedIndex].color ||
                        pieColors[selectedIndex % 9],
                  },
                  {
                    value: total - props.data[selectedIndex].value,
                    peripheral: true,
                    strokeWidth: 0,
                  },
                ]}
                radius={radius + extraRadius}
                initialAngle={startAngle}
                innerRadius={props.innerRadius || radius / 2.5}
                isBiggerPie
                selectedIndex={selectedIndex}
                setSelectedIndex={setSelectedIndex}
                paddingHorizontal={paddingHorizontal}
                paddingVertical={paddingVertical}
                extraRadius={extraRadius}
              />
            </View>
          )}
        {renderInnerCircle(
          innerRadius - inwardExtraLengthForFocused,
          inwardExtraLengthForFocused ? 0 : innerCircleBorderWidth,
        )}
        {showTooltip && tooltipSelectedIndex !== -1 ? renderTooltip() : null}
      </View>
    );
  };

  if (rotatable && !semiCircle) {
    return (
      <RotatablePie initialAngle={startAngle} size={radius * 2}>
        {[<PieChartBody key="PieChartBody" />]}
      </RotatablePie>
    );
  }
  return <PieChartBody />;
};
